import { useState, useContext, useMemo, ReactNode, FC } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Caption2, makeStyles, tokens } from '@fluentui/react-components';
import { DeleteRegular, EditRegular } from '@fluentui/react-icons';
import classNames from 'classnames';

import CommentCard from './CommentCard';
import CommentForm, { schemaComment } from './form/CommentForm';
import DateTimeLabel from './DateTimeLabel';
import ReplyInput from './replies/ReplyInput';
import Reply from './replies/Reply';
import { useArchive, useCreateReply } from '../api';
import { useEditCommentOrReply } from '../hooks/useEditCommentOrReply.hook';
import useDeleteSelection from '../hooks/useDeleteSelection.hook';
import { SignInContext, USER_GROUPS } from '@modules/users';
import { TComment, TMoreButtonItem } from '../types';
import { DocumentContext, LockedDialog, TDocument, TLockedState } from '@modules/documents';
import { TFile } from '@modules/files';
import { ConfirmDialog, TConfirmDialogState } from '@shared/components';
import { MORE_BUTTON_OPTION_KEYS, MORE_BUTTON_OPTION_LABELS } from '../constants';
import useCommentStore from '../stores/useCommentStore';
import { TInlineBlock } from '@modules/inlineBlocks';

type BaseCommentProps = {
  comment: TComment;
  item: TDocument | TFile | TInlineBlock;
  permissions: any;
  /**
   * If `initialContent` is empty, the comment is not editable.
   */
  initialContent?: string;
  displayedContent: ReactNode;
  header?: ReactNode;
};

const useStyles = makeStyles({
  userName: {
    color: tokens.colorBrandForeground1,
  },
  repliesCount: {
    color: tokens.colorNeutralForeground3,
  },
  showMore: {
    color: tokens.colorBrandForeground1,
    margin: '1rem 0',
    padding: '0',
  },
});

const BaseComment: FC<BaseCommentProps> = ({
  comment,
  item,
  permissions,
  displayedContent,
  initialContent,
  header,
}) => {
  const { user } = useContext(SignInContext);
  const documentContext = useContext(DocumentContext);
  const { selectedCommentId, setSelectedCommentId } = useCommentStore();

  // Replies filtering and “show more” logic
  const [showAllReplies, setShowAllReplies] = useState(false);
  const filteredReplies = useMemo(() => comment.replies.filter((r) => !r.archived), [comment.replies]);
  const displayedReplies = useMemo(
    () => (showAllReplies ? filteredReplies : filteredReplies.slice(-2)),
    [filteredReplies, showAllReplies]
  );
  const hasMoreThanTwo = filteredReplies.length > 2;

  // Reply form
  const replyForm = useForm({
    resolver: yupResolver(schemaComment),
    defaultValues: { text: '' },
  });
  const {
    handleSubmit: handleReplySubmit,
    reset: resetReply,
    formState: { isValid: isReplyValid },
  } = replyForm;
  const [isReplyFocused, setIsReplyFocused] = useState(false);
  const createReply = useCreateReply();
  const onSubmitReply = (data: any) => {
    createReply.mutate(
      {
        itemId: item._id,
        itemType: item.type,
        commentId: comment._id,
        content: data.text,
      },
      {
        onSuccess: () => {
          resetReply({ text: '' });
          setIsReplyFocused(false);
        },
      }
    );
  };

  // Edit comment logic
  const { isEditing, setIsEditing, editForm, handleEditSubmit, isEditable } = useEditCommentOrReply({
    itemId: item._id,
    itemType: item.type,
    commentId: comment._id,
    initialContent,
  });

  // Archive / delete logic
  const archiveComment = useArchive();
  const deleteSelection = useDeleteSelection();
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const [lockedState, setLockedState] = useState<TLockedState | undefined>();

  const handleArchiveComment = () => {
    const openDialogState: TConfirmDialogState = {
      isOpen: true,
      title: 'Delete comment?',
      content: 'Are you sure you want to delete this comment?',
      buttonLabel: 'Delete',
      onConfirm: async () => {
        try {
          const data = await archiveComment.mutateAsync({
            itemId: item._id,
            itemType: item.type,
            commentId: comment._id,
            editingDraft: documentContext?.isEdit && documentContext.editor.isEditable,
          });
          setConfirmDialogState({ ...openDialogState, isOpen: false });
          if (data && 'lockedUserId' in data) {
            setLockedState({
              isOpen: true,
              lockedUserId: data.lockedUserId,
              message:
                'A comment with a content-selection can only be deleted from the active editor or when no user is editing the document.',
            });
            return;
          }
          deleteSelection(comment._id);
        } catch {
          // The API hook displays the error. Keep the confirmation open so the user can retry.
        }
      },
    };

    setConfirmDialogState(openDialogState);
  };

  const isSelected = selectedCommentId === comment._id;
  const classes = useStyles();

  // Define the “more” button options. Note: edit is hidden if disableEdit is true.
  const moreButtonOptions: TMoreButtonItem[] = [
    {
      key: MORE_BUTTON_OPTION_KEYS.EDIT_COMMENT,
      label: MORE_BUTTON_OPTION_LABELS.EDIT_COMMENT,
      onClick: () => {
        setIsEditing(true);
        setSelectedCommentId(comment._id);
      },
      icon: EditRegular,
      disabled: !isEditable || user._id !== comment.creator,
      disabledTooltip:
        user._id !== comment.creator
          ? 'You don’t have permission to edit this comment.'
          : 'This comment is not editable.',
    },
    {
      key: MORE_BUTTON_OPTION_KEYS.ARCHIVE_COMMENT,
      label: MORE_BUTTON_OPTION_LABELS.ARCHIVE_COMMENT,
      onClick: handleArchiveComment,
      icon: DeleteRegular,
      disabled: user._id !== comment.creator && !user.groups.includes(USER_GROUPS.ADMIN),
      disabledTooltip: 'You don’t have permission to delete this comment.',
    },
  ];

  return (
    <CommentCard
      header={header}
      permissions={permissions}
      item={item}
      comment={comment}
      commentId={comment._id}
      userId={comment.creator}
      moreButtonOptions={moreButtonOptions}
    >
      {/* confirm dialog used when archiving a comment/reply */}
      <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
      <LockedDialog state={lockedState} setState={setLockedState} />
      <div className="flex flex-col items-start gap-0">
        {isEditing ? (
          <CommentForm
            form={editForm}
            isValid
            onSubmit={editForm.handleSubmit(handleEditSubmit)}
            onDismiss={() => {
              setIsEditing(false);
              editForm.reset({ text: initialContent });
            }}
          />
        ) : (
          <div className="relative w-full break-words whitespace-pre-wrap">{displayedContent}</div>
        )}
        {isSelected && (
          <>
            <DateTimeLabel createdDate={comment.createdDate} editedDate={comment.editedDate} />
            {hasMoreThanTwo && (
              <Button
                size="small"
                appearance="transparent"
                className={classes.showMore}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllReplies(!showAllReplies);
                }}
                aria-label="show more replies"
              >
                {showAllReplies ? 'Hide older replies' : 'Show older replies'}
              </Button>
            )}
            {filteredReplies.length > 0 && (
              <div className={classNames('flex flex-col gap-3 w-full', { 'pt-4': !hasMoreThanTwo })}>
                {displayedReplies.map((reply) => (
                  <Reply
                    key={reply._id}
                    commentId={comment._id}
                    item={item}
                    reply={reply}
                    setConfirmDialogState={setConfirmDialogState}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {!isSelected && filteredReplies.length > 0 && (
          <Caption2 className={classes.repliesCount}>
            {filteredReplies.length} {filteredReplies.length === 1 ? 'reply' : 'replies'}
          </Caption2>
        )}
        {isSelected && (
          <ReplyInput
            replyForm={replyForm}
            isReplyValid={isReplyValid}
            isReplyFocused={isReplyFocused}
            setIsReplyFocused={setIsReplyFocused}
            onSubmitReply={handleReplySubmit(onSubmitReply)}
          />
        )}
      </div>
    </CommentCard>
  );
};

export default BaseComment;
